// ── 월 누적 추이 SVG 차트 ─────────────────────────────────────────────────────
// 축·그리드·텍스트는 CSS 토큰(var(--border) 등) 사용 — 다크모드 자동 적용.
// 강조 라인(수입/지출)은 라이트·다크 양쪽에서 보이는 톤 고정.

import React from 'react';
import { COLORS, fmtAmount } from './constants';

const WIDTH = 880;
const HEIGHT = 340;
const PAD = { top: 20, right: 30, bottom: 40, left: 90 };

function niceMax(value) {
  if (value <= 0) return 100000;
  const exp = Math.floor(Math.log10(value));
  const base = Math.pow(10, exp);
  const norm = value / base;
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return step * base;
}

export default function CumulativeChart({ summary }) {
  if (!summary || !summary.cumulative) {
    return <div style={emptyStyle}>표시할 데이터가 없습니다.</div>;
  }
  const inflow = summary.cumulative.inflow || [];
  const outflow = summary.cumulative.outflow || [];
  const net = summary.cumulative.net || [];
  if (inflow.length === 0) {
    return <div style={emptyStyle}>표시할 데이터가 없습니다.</div>;
  }

  const innerW = WIDTH - PAD.left - PAD.right;
  const innerH = HEIGHT - PAD.top - PAD.bottom;
  const n = inflow.length;

  const maxAbs = Math.max(
    ...inflow.map((p) => Math.abs(p.cumulative)),
    ...outflow.map((p) => Math.abs(p.cumulative)),
    ...net.map((p) => Math.abs(p.cumulative)),
    1,
  );
  const yMax = niceMax(maxAbs);
  const hasNegative = net.some((p) => p.cumulative < 0);
  const yMin = hasNegative ? -yMax : 0;
  const yRange = yMax - yMin;

  const x = (i) => PAD.left + (n === 1 ? innerW / 2 : (i * innerW) / (n - 1));
  const y = (v) => PAD.top + innerH - ((v - yMin) / yRange) * innerH;

  const toPolyline = (points) => points.map((p, i) => `${x(i)},${y(p.cumulative)}`).join(' ');

  const tickCount = 5;
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) => yMin + (yRange * i) / tickCount);

  const xTickIdx = [];
  for (let d = 0; d < n; d += 1) {
    const day = Number(inflow[d].date.slice(8));
    if (day === 1 || day % 5 === 0 || d === n - 1) xTickIdx.push(d);
  }

  const zeroY = y(0);

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg
        width={WIDTH}
        height={HEIGHT}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label="자금 흐름 월 누적 추이"
        style={{ display: 'block' }}
      >
        {/* Y축 */}
        <line x1={PAD.left} x2={PAD.left} y1={PAD.top} y2={HEIGHT - PAD.bottom} stroke="var(--border)" />
        {/* 0선 */}
        <line x1={PAD.left} x2={WIDTH - PAD.right} y1={zeroY} y2={zeroY} stroke="var(--border)" />
        {/* 가로 grid + Y축 라벨 */}
        {ticks.map((t) => (
          <g key={`yt-${t}`}>
            <line x1={PAD.left} x2={WIDTH - PAD.right} y1={y(t)} y2={y(t)} stroke="var(--border)" strokeOpacity="0.5" strokeDasharray="3 3" />
            <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill="var(--text-sub)">
              {fmtAmount(t)}
            </text>
          </g>
        ))}
        {/* X축 라벨 */}
        {xTickIdx.map((i) => (
          <text key={`xt-${i}`} x={x(i)} y={HEIGHT - PAD.bottom + 16} textAnchor="middle" fontSize="11" fill="var(--text-sub)">
            {Number(inflow[i].date.slice(8))}
          </text>
        ))}
        {/* polyline — 수입 */}
        <polyline fill="none" stroke={COLORS.inflow} strokeWidth="2.5" points={toPolyline(inflow)} />
        {/* polyline — 지출 */}
        <polyline fill="none" stroke={COLORS.outflow} strokeWidth="2.5" points={toPolyline(outflow)} />
        {/* polyline — 순 (점선) */}
        <polyline fill="none" stroke={COLORS.net} strokeWidth="1.5" strokeDasharray="6 4" points={toPolyline(net)} />
      </svg>

      <div style={legendStyle}>
        <LegendDot color={COLORS.inflow}>수입 누적</LegendDot>
        <LegendDot color={COLORS.outflow}>지출 누적</LegendDot>
        <LegendDot color={COLORS.net} dashed>순 흐름</LegendDot>
      </div>
    </div>
  );
}

function LegendDot({ color, dashed, children }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 16, fontSize: 13, color: 'var(--text-sub)' }}>
      <span
        style={{
          width: 18, height: 0, borderTop: `${dashed ? '2px dashed' : '2.5px solid'} ${color}`,
        }}
      />
      {children}
    </span>
  );
}

const emptyStyle = { padding: 24, color: 'var(--text-sub)', fontSize: 14 };
const legendStyle = { marginTop: 8, paddingLeft: PAD.left, fontSize: 13 };
